import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { orderBy, noop } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import Download from 'sushiweb/Icons/all/Download';
import {
  exportCSV,
  filterCurrentData,
  getCurrentDate,
  getMonthAndYearFromDate,
  includesOption,
  indexOfOption,
  searchWithName,
} from '../../../helpers';
import {
  SearchContainerV2,
  SearchInput,
  StatusFilterAndExportCSVContainer,
  TableContainer,
} from '../../common/styles';
import DataTables from '../../common/components/DataTables/DataTables';
import { invoicesHeadColumn } from '../../common/components/DataTables/components/headColumns';
import ErrorPage from '../../common/components/ErrorPage';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
  invoicesStatusEnum,
  statusLabels,
} from '../../common/components/constants';
import StatusFilter from '../../common/components/StatusFilter';

const Invoices = ({
  currentFlow,
  invoiceData,
  fetchingInvoices,
  fetchedInvoices,
  getCompanyInvoices,
  setNoUnpaidInvocies,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const keyFields = ['serial_no'];
  const [search, onInputChange] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const settlementInvoicesData = invoiceData;
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [3],
      label: 'GENERATED_UNPAID',
      disabled: true,
    },
    {
      value: [4],
      label: 'PARTIALLY_PAID',
      disabled: true,
    },
    {
      value: [5, 6],
      label: 'PAID',
      disabled: true,
    },
    {
      value: [7],
      label: 'PAYMENT_PENDING',
      disabled: true,
    },
  ]);

  const checkUnpaidInvoicesPresent = invoices => {
    if (invoices.length === 0) {
      setNoUnpaidInvocies(true);
      return;
    }
    let unpaidInvoicesCount = 0;
    invoices.forEach(data => {
      if (
        data.status === invoicesStatusEnum.GENERATED_UNPAID ||
        data.status === invoicesStatusEnum.PARTIALLY_PAID
      ) {
        unpaidInvoicesCount += 1;
      }
    });
    if (unpaidInvoicesCount === 0) {
      setNoUnpaidInvocies(true);
    } else {
      setNoUnpaidInvocies(false);
    }
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  useEffect(() => {
    getCompanyInvoices({ flowType: currentFlow });
  }, [currentFlow]);

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 5 - 5;
      const end = currentPage * 5;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!fetchingInvoices && fetchedInvoices) {
      if (settlementInvoicesData) {
        settlementInvoicesData.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        if (settlementInvoicesData.length === 0) {
          setDisabledSearch(true);
        }
        checkUnpaidInvoicesPresent(settlementInvoicesData);
        setTableData(settlementInvoicesData);
        setSearchResults(settlementInvoicesData);
        setCurrentData(settlementInvoicesData.slice(0, 5));
      }
      setLoading(false);
    }
  }, [fetchingInvoices]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingInvoices) {
        const arr = searchWithName(settlementInvoicesData, search, keyFields);
        const availableStatuses = [];
        arr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = arr.filter(val =>
            filterCurrentData(val, currentFilters),
          );
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(arr);
        }
        handlePageChange(1);
      }
    }, 500);
    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingInvoices]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 5 - 5;
      const end = currentPage * 5;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const refreshPage = () => {
    getCompanyInvoices();
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };

  const clearFilters = () => {
    setCurrentFilters([]);
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'invoice_id', value: 'Statement No' },
      { key: 'period', value: 'Period' },
      { key: 'payable_amount', value: 'Amount payable' },
      { key: 'paid_amount', value: 'Amount paid' },
      { key: 'pending_amount', value: 'Amount pending' },
      { key: 'status', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = {};
      labelledData.invoice_id = data.invoice_id;
      labelledData.period = getMonthAndYearFromDate(data.start_date, false);
      labelledData.payable_amount = data.payable_amount;
      labelledData.paid_amount = data.paid_amount;
      labelledData.pending_amount = data.pending_amount;
      labelledData.status = statusLabels[invoicesStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `Consumption statements - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch ? (
              'Search by serial no.'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by serial no.{' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />
        <StatusFilterAndExportCSVContainer>
          <StatusFilter
            isDisabled={disabledSearch}
            clearFilters={clearFilters}
            currentFilters={currentFilters}
            options={filterStatusOptions}
            handleSelect={handleSelect}
          />
          {disabledSearch ? (
            <Download color='grey' style={{ padding: '10px 20px 20px 0px' }} />
          ) : (
            <Tooltip
              position='top'
              label='Download the consumption statement(s)'
              appearance='light'
              size='big'
              style={{
                fontSize: '12px',
                opacity: 0.8,
                marginRight: '10px',
                marginLeft: '-30px',
              }}
            >
              <Download
                color='grey'
                onClick={exportSearchResultCSV}
                style={downloadCSVStyle}
              />
            </Tooltip>
          )}
        </StatusFilterAndExportCSVContainer>
      </SearchContainerV2>
      <TableContainer>
        {!fetchedInvoices ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            data={currentData}
            columns={invoicesHeadColumn}
            loading={fetchingInvoices || loading}
            pagination
            handlePageChange={handlePageChange}
            totalRows={searchResults ? searchResults.length : 0}
            onSort={handleSort}
            emptyStateTitle='No consumptions statements'
            paginationNumber={5}
          />
        )}
      </TableContainer>
    </>
  );
};

Invoices.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  invoiceData: PropTypes.objectOf(PropTypes.any),
  getCompanyInvoices: PropTypes.func,
  fetchingInvoices: PropTypes.bool,
  fetchedInvoices: PropTypes.bool,
  setNoUnpaidInvocies: PropTypes.func,
};

Invoices.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  invoiceData: {},
  getCompanyInvoices: noop,
  fetchingInvoices: true,
  fetchedInvoices: true,
  setNoUnpaidInvocies: noop,
};

export default Invoices;
